import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  ProjectConnectionCreateRequest,
  ProjectConnectionDetailsDto,
  ProjectConnectionEditRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddProjectConnection = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (connection: ProjectConnectionCreateRequest) => api.postProjectConnectionsV1(connection),
    onSuccess() {
      showFlashToast({
        message: t("page.project-connections.actions.create.notification.success"),
        variant: "success",
      });
    },
    onError() {
      showFlashToast({
        message: t("page.project-connections.actions.create.notification.error"),
        variant: "alert",
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
  });
};

const useUpdateProjectConnection = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ connectionId, connection }: { connectionId: string; connection: ProjectConnectionEditRequest }) =>
      api.putProjectConnectionsV1(connectionId, connection),
    onSuccess() {
      showFlashToast({
        message: t("page.project-connections.actions.edit.notification.success"),
        variant: "success",
      });
    },
    onError() {
      showFlashToast({
        message: t("page.project-connections.actions.edit.notification.error"),
        variant: "alert",
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
  });
};

const useDeleteProjectConnection = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (connection: ProjectConnectionDetailsDto) => api.deleteProjectConnectionsByIdV1(connection.id),
    onSuccess() {
      showFlashToast({
        message: t("page.project-connections.actions.delete.notification.success"),
        variant: "success",
      });
    },
    onError() {
      showFlashToast({
        message: t("page.project-connections.actions.delete.notification.error"),
        variant: "alert",
      });
    },
    onSettled() {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS });
    },
  });
};

const useJoinProjectConnections = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (connectionIds: string[]) =>
      Promise.all(connectionIds.map((connectionId) => api.postProjectConnectionsJoinV1(connectionId))),
    onError() {
      showFlashToast({
        message: t("page.project-connection.join.error"),
        variant: "alert",
      });
    },
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SELF(projectId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTION_INVITES(projectId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PROJECT_CONNECTIONS }),
      ]);
    },
  });
};

export const projectConnectionMutations = {
  useAddProjectConnection,
  useUpdateProjectConnection,
  useDeleteProjectConnection,
  useJoinProjectConnections,
};
